import React, { forwardRef, useEffect, useLayoutEffect } from 'react';
import Type from 'prop-types';
import cn from 'classnames';
import { ButtonBase, isMuiElement, useForkRef } from '@mui/material';
import ListContext from '../List/ListContext';
import styles from './ListItem.module.css';

const useEnhancedEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const ListItem = forwardRef(function ListItem(props, ref) {
  const {
    alignItems = 'center',
    autoFocus = false,
    button = false,
    children: childrenProp,
    className,
    component: componentProp,
    ContainerComponent = 'li',
    ContainerProps: { className: ContainerClassName, ...ContainerProps } = {},
    dense = false,
    disabled = false,
    disableGutters = false,
    divider = false,
    focusVisibleClassName,
    selected = false,
    ...other
  } = props;

  const context = React.useContext(ListContext);
  const childContext = {
    dense: dense || context.dense || false,
    alignItems,
  };
  const listItemRef = React.useRef(null);
  useEnhancedEffect(() => {
    if (autoFocus && listItemRef.current) {
      listItemRef.current.focus();
    }
  }, [autoFocus]);

  const children = React.Children.toArray(childrenProp);
  const hasSecondaryAction =
    children.length &&
    isMuiElement(children[children.length - 1], ['ListItemSecondaryAction']);

  const handleRef = useForkRef(listItemRef, ref);

  const componentProps = {
    className: cn(
      styles.root,
      {
        [styles.dense]: childContext.dense,
        [styles.gutters]: !disableGutters,
        [styles.divider]: divider,
        [styles.disabled]: disabled,
        [styles.button]: button,
        [styles.alignItemsFlexStart]: alignItems === 'flex-start',
        [styles.secondaryAction]: hasSecondaryAction,
        [styles.selected]: selected,
      },
      className,
    ),
    disabled,
    ...other,
  };
  let Component = componentProp || 'li';

  if (button) {
    componentProps.component = componentProp || 'div';
    componentProps.focusVisibleClassName = cn(
      styles.focusVisible,
      focusVisibleClassName,
    );
    Component = ButtonBase;
  }

  if (hasSecondaryAction) {
    // Use div by default.
    Component = !componentProps.component && !componentProp ? 'div' : Component;

    // Avoid nesting of li > li.
    if (ContainerComponent === 'li') {
      if (Component === 'li') {
        Component = 'div';
      } else if (componentProps.component === 'li') {
        componentProps.component = 'div';
      }
    }

    return (
      <ListContext.Provider value={childContext}>
        <ContainerComponent
          className={cn(styles.container, ContainerClassName)}
          ref={handleRef}
          {...ContainerProps}
        >
          <Component {...componentProps}>{children}</Component>
          {children.pop()}
        </ContainerComponent>
      </ListContext.Provider>
    );
  }

  return (
    <ListContext.Provider value={childContext}>
      <Component ref={handleRef} {...componentProps}>
        {children}
      </Component>
    </ListContext.Provider>
  );
});

/* eslint-disable */
ListItem.propTypes = {
  alignItems: Type.oneOf(['flex-start', 'center']),
  autoFocus: Type.bool,
  button: Type.bool,
  children: Type.node,
  className: Type.string,
  component: Type.elementType,
  ContainerComponent: Type.elementType,
  ContainerProps: Type.object,
  dense: Type.bool,
  disabled: Type.bool,
  disableGutters: Type.bool,
  divider: Type.bool,
  focusVisibleClassName: Type.string,
  selected: Type.bool,
};
/* eslint-enable */

export default ListItem;
